/**
 * Inline header product search with predictive dropdown.
 */
class HeaderProductSearch extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #debounceTimer;

  /** @type {number} */
  #activeIndex = -1;

  connectedCallback() {
    this.input = this.querySelector('.header-product-search__input');
    this.results = this.querySelector('.header-product-search__results');
    this.form = this.querySelector('form');
    this.btn = this.querySelector('.header-product-search__btn');

    if (!this.input || !this.results || !this.form) return;

    // Always open — no click-to-expand
    this.classList.add('is-expanded');

    this.input.addEventListener('input', this.#onInput);
    this.input.addEventListener('keydown', this.#onKeyDown);
    this.form.addEventListener('submit', this.#onSubmit);
    this.results.addEventListener('wheel', this.#onResultsWheel, { passive: false });
    this.results.addEventListener('touchmove', this.#onResultsTouchMove, { passive: false });
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeyDown);
    this.results?.removeEventListener('wheel', this.#onResultsWheel);
    this.results?.removeEventListener('touchmove', this.#onResultsTouchMove);
    clearTimeout(this.#debounceTimer);
    this.#abortController?.abort();
  }

  get currency() {
    return this.dataset.currency || 'TRY';
  }

  get locale() {
    return this.dataset.locale || 'tr-TR';
  }

  get searchUrl() {
    return this.dataset.searchUrl || '/search';
  }

  get predictiveUrl() {
    return this.dataset.predictiveUrl || '/search/suggest';
  }

  #expand = () => {
    // Kept for compatibility — search stays permanently open
    this.classList.add('is-expanded');
  };

  #collapse = () => {
    // Only close results dropdown; keep the input bar open
    this.#hideResults();
  };

  #onDocumentClick = (event) => {
    if (!this.contains(event.target)) {
      this.#collapse();
    }
  };

  #onDocumentKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#hideResults();
      this.input?.blur();
    }
  };

  /**
   * Keep wheel scroll inside results — don't scroll the page.
   * Slight dampening only.
   * @param {WheelEvent} event
   */
  #onResultsWheel = (event) => {
    const el = this.results;
    if (!el) return;

    event.preventDefault();
    event.stopPropagation();

    let delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 16;
    if (event.deltaMode === 2) delta *= el.clientHeight;

    el.scrollTop += delta * 0.85;
  };

  /**
   * @param {TouchEvent} event
   */
  #onResultsTouchMove = (event) => {
    event.stopPropagation();
  };

  #onInput = () => {
    this.#expand();
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      const query = this.input?.value.trim() || '';
      if (query.length < 2) {
        this.#hideResults();
        return;
      }
      this.#fetchResults(query);
    }, 220);
  };

  #onKeyDown = (event) => {
    const options = this.#getOptions();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!options.length) return;
      this.#activeIndex = (this.#activeIndex + 1) % options.length;
      this.#syncActiveOption();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!options.length) return;
      this.#activeIndex = this.#activeIndex <= 0 ? options.length - 1 : this.#activeIndex - 1;
      this.#syncActiveOption();
      return;
    }

    if (event.key === 'Enter' && this.#activeIndex >= 0 && options[this.#activeIndex]) {
      event.preventDefault();
      options[this.#activeIndex].click();
    }
  };

  #onSubmit = (event) => {
    const query = this.input?.value.trim() || '';
    if (!query) {
      event.preventDefault();
      this.input?.focus();
    }
  };

  /**
   * @param {string} query
   */
  async #fetchResults(query) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const url = new URL(this.predictiveUrl + '.json', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      this.#renderResults(products, query);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#hideResults();
    }
  }

  /**
   * @param {Array<Record<string, any>>} products
   * @param {string} query
   */
  #renderResults(products, query) {
    if (!this.results) return;

    this.#activeIndex = -1;
    this.results.scrollTop = 0;

    if (!products.length) {
      this.results.innerHTML = `<li><p class="header-product-search__empty">Sonuç bulunamadı</p></li>`;
      this.results.hidden = false;
      this.input?.setAttribute('aria-expanded', 'true');
      this.classList.add('is-results-open');
      return;
    }

    const items = products
      .map((product) => {
        const title = this.#escape(product.title || '');
        const url = this.#escape(product.url || '#');
        const image =
          product.image ||
          product.featured_image?.url ||
          product.featured_image ||
          '';
        const price = this.#formatPrice(product.price ?? product.price_min);

        const thumb = image
          ? `<img class="header-product-search__thumb" src="${this.#escape(image)}" alt="" loading="lazy" width="48" height="48">`
          : `<span class="header-product-search__thumb--empty" aria-hidden="true">◇</span>`;

        return `
          <li>
            <a href="${url}" class="header-product-search__option" role="option">
              <span class="header-product-search__thumb-wrap">${thumb}</span>
              <span class="header-product-search__meta">
                <span class="header-product-search__name">${title}</span>
                <span class="header-product-search__price">${price}</span>
              </span>
            </a>
          </li>
        `;
      })
      .join('');

    const viewAllUrl = `${this.searchUrl}?q=${encodeURIComponent(query)}`;

    this.results.innerHTML = `
      ${items}
      <li>
        <a href="${viewAllUrl}" class="header-product-search__view-all">Tüm sonuçları gör</a>
      </li>
    `;
    this.results.hidden = false;
    this.input?.setAttribute('aria-expanded', 'true');
    this.classList.add('is-results-open');
  }

  #hideResults() {
    if (!this.results) return;
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.#activeIndex = -1;
    this.results.scrollTop = 0;
    this.input?.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-results-open');
  }

  #getOptions() {
    return Array.from(this.results?.querySelectorAll('.header-product-search__option') || []);
  }

  #syncActiveOption() {
    const options = this.#getOptions();
    options.forEach((option, index) => {
      if (index === this.#activeIndex) {
        option.setAttribute('aria-selected', 'true');
        option.scrollIntoView({ block: 'nearest' });
      } else {
        option.removeAttribute('aria-selected');
      }
    });
  }

  /**
   * Predictive Search JSON returns prices as major currency units (e.g. "1100.00"),
   * not cents — do not divide by 100.
   * @param {string | number | null | undefined} value
   */
  #formatPrice(value) {
    if (value == null || value === '') return '';

    const major = Number(String(value).replace(',', '.'));
    if (Number.isNaN(major)) return '';

    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
      }).format(major);
    } catch {
      return `${major.toFixed(2)} ${this.currency}`;
    }
  }

  /**
   * @param {string} value
   */
  #escape(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('header-product-search')) {
  customElements.define('header-product-search', HeaderProductSearch);
}
